import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import { amountFormat } from "../../utils";
import { getSymbolCode, graph_millify } from "../../config/helper";

const LineGraph = ({ id, data = [] }) => {
  const containerRef = useRef(null);
  const symbol_code = getSymbolCode();
  const [dimensions, setDimensions] = useState({ width: 0, height: 300 });

  // Observe size changes
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: 300, // You can make height dynamic too if needed
        });
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, []);

  // Draw graph when dimensions or data change
  useEffect(() => {
    if (!data.length || dimensions.width === 0) return;

    // Clear previous graph
    d3.select(`#${id}`).select("svg").remove();

    const { width, height } = dimensions;

    const scale = d3
      .scaleBand()
      .domain(data.map((ele) => ele.day))
      .range([0, width])
      .padding(0.2);

    const scaleL = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => d.txnValue)])
      .range([(height - 40) * 0.95, 2]);

    const x_axis = d3.axisBottom(scale).tickSize(0);
    const y_axisL = d3
      .axisLeft(scaleL)
      .tickFormat((d) => graph_millify(d, true, true))
      .tickSize(0);

    const svg = d3
      .select(`#${id}`)
      .append("svg")
      .attr("width", width)
      .attr("height", height);

    svg
      .append("g")
      .attr("transform", `translate(10,${height - 25})`)
      .attr("style", "color:#dbe1e9")
      .call(x_axis);

    svg
      .append("g")
      .attr("transform", `translate(${40},18)`)
      .attr("style", "color:#dbe1e9")
      .call(y_axisL);

    const tooltip = d3
      .select(`#${id}`)
      .append("div")
      .attr("class", `bar-line-tooltip isTooltip`)
      .style("display", "flex")
      .style("opacity", 0)
      .style("position", "absolute");

    // Line and area setup
    const line = d3
      .line()
      .x((d) => scale(d.day) + width / data.length / 2)
      .y((d) => scaleL(d.txnValue) + 20)
      .curve(d3.curveMonotoneX);

    const area = d3
      .area()
      .x((d) => scale(d.day) + width / data.length / 2)
      .y0(height)
      .y1((d) => scaleL(d.txnValue) + 20)
      .curve(d3.curveMonotoneX);

    const gradient = svg
      .append("defs")
      .append("linearGradient")
      .attr("id", "areaGradient1")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    gradient
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#F48C32")
      .attr("stop-opacity", 0.4);

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#ffffff")
      .attr("stop-opacity", 0);

    svg
      .append("path")
      .datum(data)
      .attr("d", area)
      .attr("fill", "url(#areaGradient1)")
      .style("transform", "translate(10px, 0px)");

    svg
      .append("path")
      .datum(data)
      .attr("d", line)
      .attr("stroke", "#F5994B")
      .style("stroke-width", 1)
      .style("transform", "translate(10px, 0px)")
      .attr("fill", "none");

    svg
      .selectAll(".dot")
      .data(data)
      .enter()
      .append("circle")
      .attr("class", "dot")
      .attr("cx", (d) => scale(d.day) + width / data.length / 2)
      .attr("cy", (d) => scaleL(d.txnValue) + 20)
      .attr("r", 4)
      .attr("fill", "#F5994B")
      .style("transform", "translate(10px, 0px)")
      .on("mouseover", function (event, d) {
        tooltip
          .html(
            `<span>${d.label}</span><span>${symbol_code} ${amountFormat(
              d.txnValue
            )}</span>`
          )
          .style("left", event.offsetX + "px")
          .style("top", event.offsetY - 40 + "px")
          .style("opacity", 1);
      })
      .on("mouseout", () => {
        tooltip.style("opacity", 0);
      });
  }, [data, dimensions]);

  return (
    <div
      className="chart-wrapper graph-container"
      style={{ width: "100%", position: "relative" }}
      ref={containerRef}
    >
      <div id={id} className="draw_graph_container"></div>
    </div>
  );
};

export default LineGraph;
